import React, { useState, useEffect } from 'react';
import { Room } from 'livekit-client';
import { RoomAudioRenderer } from '@livekit/components-react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { TestingConversationView } from '../TestingConversationView';
import { ControlBar } from '../ControlBar';
import { NoAgentNotification } from '../NoAgentNotification';
import { useSimpleVoiceAssistantStyles } from './SimpleVoiceAssistant.styles';
import { VoiceState } from '../../types/VoiceState';
import { AllParticipantStates } from '../../types/roomManager.types';
import { CopyCallIdButton } from '@shared/components/CopyCallIdButton';

interface SimpleVoiceAssistantProps {
  onConnectButtonClicked: () => void;
  room: Room;
  agentName?: string;
  callId?: string | null;
  participantStates?: AllParticipantStates;
}

export const SimpleVoiceAssistant = ({
  onConnectButtonClicked,
  room,
  agentName = 'Agent',
  callId,
  participantStates,
}: SimpleVoiceAssistantProps) => {
  const { classes } = useSimpleVoiceAssistantStyles();
  const { t } = useTranslation();
  const [voiceState, setVoiceState] = useState<VoiceState>(VoiceState.DISCONNECTED);

  useEffect(() => {
    const handleRoomStateChange = () => {
      const roomState = room.state;

      if (roomState === 'disconnected') {
        setVoiceState(VoiceState.DISCONNECTED);
      } else if (roomState === 'connecting') {
        setVoiceState(VoiceState.CONNECTING);
      } else if (roomState === 'connected') {
        setVoiceState(VoiceState.CONNECTED);
        setTimeout(() => {
          setVoiceState(VoiceState.LISTENING);
        }, 1000);
      }
    };

    handleRoomStateChange();

    room.on('connectionStateChanged', handleRoomStateChange);
    room.on('connected', () => {
      setVoiceState(VoiceState.CONNECTED);
    });
    room.on('disconnected', () => {
      setVoiceState(VoiceState.DISCONNECTED);
    });

    return () => {
      room.off('connectionStateChanged', handleRoomStateChange);
      room.off('connected', handleRoomStateChange);
      room.off('disconnected', handleRoomStateChange);
    };
  }, [room]);

  return (
    <div className={`${classes.connectedContainer} ${classes.slideUp}`}>
      <div className={classes.transcriptionContainer}>
        {callId && (
          <Box className={classes.callIdHeader}>
            <Typography variant="body2">
              {t('webSocketCall.callId')}: {callId}
            </Typography>
            <CopyCallIdButton callId={callId} />
          </Box>
        )}
        <div className={classes.conversationView}>
          <TestingConversationView agentName={agentName} placeholderMode="waiting" room={room} />
        </div>
      </div>
      <div className={classes.controlBarContainer}>
        <ControlBar
          state={voiceState}
          onConnectButtonClicked={onConnectButtonClicked}
          room={room}
          participantStates={participantStates}
        />
      </div>
      <RoomAudioRenderer />
      <NoAgentNotification state={voiceState} />
    </div>
  );
};
